import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material';

import { useMytaverse } from '@providers/MytaverseProvider';
import { ScreenSizes } from '@interfaces';
import { CurrentScreenEnum } from '@providers/MytaverseProvider/interfaces';
import { useAssetStore } from '@components/AssetPreloader/assetsCasheContext';

const useBackground = () => {
  const { appTheme, currentScreen } = useMytaverse();

  const { cachedAssets } = useAssetStore();

  const [backgroundImage, setBackgroundImage] = useState('');
  const [backgroundImageMobile, setBackgroundImageMobile] = useState('');

  useEffect(() => {
    let backgroundImage = '';
    let mobileBackgroundImage = '';

    switch (currentScreen) {
      case CurrentScreenEnum.DEFAULT:
        backgroundImage = appTheme.newDefaultBackground;
        mobileBackgroundImage = appTheme.newDefaultBackgroundMobile;
        break;
      case CurrentScreenEnum.AVATAR_SELECTION:
      case CurrentScreenEnum.AVATURN:
        backgroundImage = appTheme.AvatarSelectionBackground;
        mobileBackgroundImage = appTheme.AvatarSelectionBackgroundMobile;
        break;
      case CurrentScreenEnum.DEVICES_TEST_MICROPHONE:
        backgroundImage = appTheme.DevicesTestMicrophoneBackground;
        mobileBackgroundImage = appTheme.DevicesTestMicrophoneBackgroundMobile;
        break;
      case CurrentScreenEnum.DEVICES_TEST_SPEAKER:
        backgroundImage = appTheme.DevicesTestSpeakerBackground;
        mobileBackgroundImage = appTheme.DevicesTestSpeakerBackgroundMobile;
        break;
      case CurrentScreenEnum.DEVICES_TEST_CONNECTION:
        backgroundImage = appTheme.DevicesTestConnectionBackground;
        mobileBackgroundImage = appTheme.DevicesTestConnectionBackgroundMobile;
        break;
      case CurrentScreenEnum.DEVICES_TEST_WEBCAM:
        backgroundImage = appTheme.DevicesTestWebcamBackground;
        mobileBackgroundImage = appTheme.DevicesTestWebcamBackgroundMobile;
        break;
    }

    backgroundImage =
      backgroundImage && cachedAssets[backgroundImage]
        ? cachedAssets[backgroundImage]
        : backgroundImage;

    mobileBackgroundImage =
      mobileBackgroundImage && cachedAssets[mobileBackgroundImage]
        ? cachedAssets[mobileBackgroundImage]
        : mobileBackgroundImage;

    setBackgroundImage(backgroundImage);
    setBackgroundImageMobile(mobileBackgroundImage);
  }, [currentScreen, cachedAssets]);

  return [backgroundImage, backgroundImageMobile];
};

export const useStyles = (moreSpaceAbove: boolean) => {
  const { breakpoints } = useTheme();

  const [backgroundImage, mobileBackgroundImage] = useBackground();

  return {
    root: {
      backgroundImage: backgroundImage ? `url(${backgroundImage})` : '',
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      padding: '0 30px',
      height: '100vh',
      maxHeight: '100dvh',
      [breakpoints.down(ScreenSizes.Tablet)]: {
        backgroundImage: mobileBackgroundImage
          ? `url(${mobileBackgroundImage})`
          : '',
        backgroundPosition: 'top',
        // height: 'auto',
        padding: '0 15px',
      },
    },
    wrapbox: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      [breakpoints.down(ScreenSizes.Tablet)]: {
        height: 'calc(100vh - 62px)',
        paddingBottom: '20px',
        overflow: 'hidden',
      },
    },
    content: {
      width: 730,
      backgroundColor: 'white',
      borderRadius: 5,
      padding: '40px',
      height: 'calc(-128px + 100vh)',
      maxHeight: 'calc(-128px + 100dvh)',
      display: 'grid',
      gridTemplateRows: 'auto 1fr',
      [breakpoints.down(ScreenSizes.PcLarge)]: {
        paddingBottom: '30px',
      },
      [breakpoints.down(ScreenSizes.Pc)]: {
        height: 'calc(-118px + 100vh)',
        maxHeight: 'calc(-118px + 100dvh)',
      },
      [breakpoints.down(ScreenSizes.TabletHorizontal)]: {
        width: 530,
        padding: '30px',
        height: 'calc(-98px + 100vh)',
        maxHeight: 'calc(-98px + 100dvh)',
      },
      [breakpoints.down(ScreenSizes.Tablet)]: {
        width: '100%',
        padding: '20px',
        position: 'absolute',
        bottom: 0,
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '65vh',
        height: '100%',
      },

      [`@media screen and (orientation:landscape)
       and (max-device-height: 760px)
       and (max-device-width: 1180px)`]: {
        height: 'calc(-88px + 100vh)',
        maxHeight: 'calc(-88px + 100dvh)',
      },
      [`@media screen and (orientation:landscape)
       and (max-device-height: 650px)
       and (max-device-width: 1180px)`]: {
        height: 'calc(-70px + 100vh)',
        maxHeight: 'calc(-70px + 100dvh)',
      },
      [`@media screen and (orientation:landscape)
       and (max-device-height: 570px)
       and (max-device-width: 900px)`]: {
        height: 'calc(-50px + 100vh)',
        maxHeight: 'calc(-50px + 100dvh)',
      },
      [`@media only screen and (max-height: 575.98px) and (max-device-width: 750px) and (orientation: landscape)`]:
        {
          height: 'calc(-65px + 100vh)',
          maxHeight: 'calc(-65px + 100dvh)',
        },
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'top',
    },
    skip: {
      textDecoration: 'underline',
      cursor: 'pointer',
    },
    children: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },
  };
};
